import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import TreatmentProtocolsStepsPanel from 'components/TreatmentProtocols/TreatmentProtocolsStepsPanel';
import Button from 'components/Button';
// redux
import { connect } from 'react-redux';
import { setTableParams } from 'reducers/tableParams';
// hooks
import { tableParamsSelector } from 'hooks/useDataTable';
// endpoints
import { getPigGroupTreatmentProtocolProductSteps } from 'endpoints/treatmentProtocols';
// constants
import { tableNames } from 'utils/constants';

const REFRESH_KEY = 'barnsheets_tp_refresh';

const BarnSheetsTreatmentProtocols = ({
  isOnline,
  setTableParams,
  tableParams,
  pigGroup,
  params: { id },
  user,
  router,
  location
}) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdminOrSuperAdmin = () => {
    if (!user || !user.roles_map) return false;
    return !!(user.roles_map.admin || user.roles_map.super_admin);
  };

  const isManagerOnCurrentFarm = () => {
    if (!pigGroup || !pigGroup.farm || !pigGroup.farm.id) return false;
    if (!user || !user.roles_map || !user.roles_map.manager) return false;

    const farmId = pigGroup.farm.id;
    const farmManagerId = pigGroup.farm.manager_id;

    const managerFarmIds = Array.isArray(user.roles_map.manager.farm_ids)
      ? user.roles_map.manager.farm_ids
      : [];
    const managerCompanyIds = Array.isArray(user.roles_map.manager.company_ids)
      ? user.roles_map.manager.company_ids
      : [];

    return managerFarmIds.includes(farmId) || (farmManagerId ? managerCompanyIds.includes(farmManagerId) : false);
  };

  const isVetOnCurrentFarm = () => {
    if (!pigGroup || !pigGroup.farm || !pigGroup.farm.id) return false;
    if (!user || !user.roles_map || !user.roles_map.vet) return false;

    const farmId = pigGroup.farm.id;
    const farmManagerId = pigGroup.farm.manager_id;

    const vetFarmIds = Array.isArray(user.roles_map.vet.farm_ids)
      ? user.roles_map.vet.farm_ids
      : [];
    const vetCompanyIds = Array.isArray(user.roles_map.vet.company_ids)
      ? user.roles_map.vet.company_ids
      : [];

    return vetFarmIds.includes(farmId) || (farmManagerId ? vetCompanyIds.includes(farmManagerId) : false);
  };

  const isTreatmentProtocolsEnabled = () => {
    if (!user || !user.current_company) return false;

    return user.current_company.treatment_protocols &&
           user.current_company.admin_treatment_protocols &&
           !user.current_company.everypig_basic;
  };

  const isGroupOpened = pigGroup?.status === 'opened';
  const canCreateTreatmentProtocol =
    isGroupOpened &&
    isTreatmentProtocolsEnabled() &&
    (isAdminOrSuperAdmin() || isManagerOnCurrentFarm() || isVetOnCurrentFarm());

  useEffect(() => {
    const shouldRefresh =
      localStorage.getItem(REFRESH_KEY) === 'true' ||
      location.state?.shouldRefresh;

    if (shouldRefresh) {
      localStorage.removeItem(REFRESH_KEY);
      setRefreshKey(Date.now());

      router.replace({
        ...location,
        state: {}
      });
    }
  }, [location]);

  const handleCreateTreatmentProtocol = () => {
    const farmId = pigGroup?.farm?.id;
    if (!farmId) return;

    localStorage.setItem(REFRESH_KEY, 'true');

    router.push({
      pathname: `/treatment-protocols-farms/${farmId}/groups/${id}/treatment-protocols/create`,
      query: { returnUrl: location.pathname },
      state: { fromBarnSheets: true }
    });
  };

  const headerActions = canCreateTreatmentProtocol ? (
    <Button
      primary
      small
      onClick={handleCreateTreatmentProtocol}
      style={{
        minWidth: '180px',
        whiteSpace: 'nowrap'
      }}
    >
      <i className="fa fa-plus" style={{ marginRight: '6px' }} />
      <FormattedMessage id="general.createTreatmentProtocol" />
    </Button>
  ) : null;

  return (
    <TreatmentProtocolsStepsPanel
      key={refreshKey || 'default'}
      isOnline={isOnline}
      fetchTreatmentProtocolsSteps={(params) => getPigGroupTreatmentProtocolProductSteps(id, params)}
      setTableParams={(params) => setTableParams(tableNames.barnSheetsGroupsTPProductSteps, params)}
      pigGroup={pigGroup}
      tableParams={tableParams}
      title={<FormattedMessage id="general.treatmentProtocols" />}
      headerActions={headerActions}
    />
  );
};

BarnSheetsTreatmentProtocols.propTypes = {
  isOnline: T.bool.isRequired,
  setTableParams: T.func.isRequired,
  tableParams: T.object.isRequired,
  pigGroup: T.object.isRequired,
  params: T.object.isRequired,
  user: T.object.isRequired,
  router: T.object.isRequired,
  location: T.object.isRequired,
};

export default connect(
  (state) => ({
    isOnline: state.network.isOnline,
    tableParams: tableParamsSelector(state, tableNames.barnSheetsGroupsTPProductSteps),
    user: state.auth.user,
  }), { setTableParams }
)(BarnSheetsTreatmentProtocols);
